import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import LinkPicker from './LinkPicker';
import { getCompletedPolicies } from '../../../services/api/policyService';
import { LinkedPolicyDTO } from '../../../services/apiService';

interface PolicyPickerProps {
    compact?: boolean;
    value: LinkedPolicyDTO[];
    onChange: (policies: LinkedPolicyDTO[]) => void;
}

const notExpired = (policy: LinkedPolicyDTO) => {
    if (!policy.expiryDate) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(policy.expiryDate) >= today;
};

const loadActivePolicies = async (): Promise<LinkedPolicyDTO[]> => {
    const result = await getCompletedPolicies({ limit: 500 });
    return (result.data ?? [])
        .map((p) => ({
            _id: p._id,
            document_id: { title: p.title },
            effectiveDate: p.effectiveDate,
            expiryDate: p.expiryDate,
        }))
        .filter(notExpired);
};

export default function PolicyPicker({
    value,
    onChange,
    compact,
}: PolicyPickerProps) {
    const { t } = useTranslation();
    return (
        <LinkPicker
            compact={compact}
            value={value}
            onChange={onChange}
            load={loadActivePolicies}
            label={(p) =>
                p.document_id?.title?.trim() || t('pickers.policies.unnamed')
            }
            icon={FileText}
            text={{
                empty: t('pickers.policies.empty'),
                add: t('pickers.policies.add'),
                search: t('pickers.policies.search'),
                noMatching: t('pickers.policies.noMatching'),
                remove: t('pickers.policies.remove'),
                loadFailed: t('pickers.policies.loadFailed'),
            }}
        />
    );
}
